import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, ListPlus } from "lucide-react";

import { Modal } from "../../../shared/ui/modal";
import SectionTabs from "../../../shared/ui/tabs/SectionTabs";
import CsvImportPanel from "../../../shared/ui/csv/CsvImportPanel";
import { usePastSessionImport } from "../application/usePastSessionImport";
import {
    pastSessionColumns,
    readPastSessions,
    SUBJECT_COLUMN_KEYS,
    type ImportCourseOption,
    type ImportSubjectOption,
    type ImportPractitionerOption,
    type ImportTarget,
    type PastSessionInput,
} from "../domain/pastSessionImport";
import PastSessionRowsForm from "./PastSessionRowsForm";

/** The API's per-row refusals this screen has words for. */
const KNOWN_ERRORS = new Set([
    "patient_not_found",
    "patient_ambiguous",
    "couple_not_found",
    "couple_ambiguous",
    "group_not_found",
    "group_ambiguous",
    "practitioner_not_found",
    "session_in_the_future",
    "cancelled_by_requires_cancelled",
    "course_not_found",
    "course_module_not_found",
    "session_has_no_course",
    "course_module_not_in_course",
]);

type Tab = "manual" | "csv";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    target: ImportTarget;
    /** The patients, couples and groups a session can be with. */
    subjects: ImportSubjectOption[];
    /** A centre names who held each session. */
    practitioners?: ImportPractitionerOption[];
    /** A training centre names the module each session worked on. */
    courses?: ImportCourseOption[];
    /**
     * Opened from one patient's, couple's or group's record: every row is
     * about them — no picker by hand, no column saying who in a file.
     */
    fixedSubject?: Pick<ImportSubjectOption, "kind" | "id">;
    /** Told once any session was created, to read the record's list again. */
    onImported?: () => void;
}

/**
 * Bringing in sessions that took place before Alchely, so the reports count
 * them: typed in row by row, or from a spreadsheet. Nothing clinical comes in
 * — when, how long, how, whether it was held and paid, and the module.
 */
export default function PastSessionImportModal({
    isOpen,
    onClose,
    target,
    subjects,
    practitioners,
    courses,
    fixedSubject,
    onImported,
}: Props) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<Tab>("manual");
    const { send: sendRows, sending } = usePastSessionImport(target);
    const send = async (rows: PastSessionInput[]) => {
        const results = await sendRows(rows);
        if (results.some((result) => !result.error)) onImported?.();
        return results;
    };
    const withPractitioner = target.kind === "centre";
    const withCourse = Boolean(courses && courses.length > 0);
    const errorMessage = (code: string) =>
        t(KNOWN_ERRORS.has(code) ? `pastSessions.errors.${code}` : "pastSessions.errors.generic");
    const csvOptions = { withPractitioner, withCourse, fixedSubject };

    const hint = (
        <div className="flex flex-col gap-2">
            <p>{t(fixedSubject ? "pastSessions.csvRecordHint" : "pastSessions.csvHint")}</p>
            {withCourse && <p>{t("pastSessions.csvCourseHint")}</p>}
        </div>
    );

    const rowsForm = (
        <PastSessionRowsForm
            subjects={subjects}
            fixedSubject={fixedSubject ? `${fixedSubject.kind}:${fixedSubject.id}` : undefined}
            practitioners={withPractitioner ? practitioners ?? [] : undefined}
            courses={withCourse ? courses : undefined}
            send={send}
            sending={sending}
            errorMessage={errorMessage}
        />
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("pastSessions.title")}
            className="mx-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <h3 className="text-lg font-semibold text-content-primary">{t("pastSessions.title")}</h3>
            <p className="mb-4 mt-1 text-sm text-content-tertiary">{t("pastSessions.subtitle")}</p>
            <SectionTabs<Tab>
                label={t("pastSessions.title")}
                activeTab={tab}
                onChange={setTab}
                tabs={[
                    { id: "manual", label: t("pastSessions.manualTab"), icon: ListPlus },
                    { id: "csv", label: t("pastSessions.csvTab"), icon: FileUp },
                ]}
            />
            <div role="tabpanel" aria-label={t(tab === "manual" ? "pastSessions.manualTab" : "pastSessions.csvTab")}>
                {tab === "manual" ? (
                    rowsForm
                ) : (
                    <CsvImportPanel<PastSessionInput>
                        columns={pastSessionColumns(csvOptions).map((column) => ({
                            ...column,
                            description: t(`pastSessions.columns.${column.key}`),
                            ...(SUBJECT_COLUMN_KEYS.includes(column.key)
                                ? { requirement: t("pastSessions.oneOfThree") }
                                : {}),
                        }))}
                        read={(table) => readPastSessions(table, csvOptions)}
                        templateName={t("pastSessions.templateName")}
                        hint={hint}
                        onImport={send}
                        describe={(row) =>
                            `${row.patient_email ?? row.couple_name ?? row.couple_email ?? row.group_name ?? ""} · ${row.date} ${row.hour}`
                        }
                        errorMessage={errorMessage}
                        submitLabel={(count) => t("pastSessions.submit", { count })}
                    />
                )}
            </div>
        </Modal>
    );
}
